import {
  Dialog,
  DialogBareContent,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  useErrorToast,
} from '@h2hspark/ui';
import { ScanLine, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useScannerAvailability } from '../../member/use-scanner-availability';
import { INITIAL_SCAN_STAGE, type ScanStage } from '../member-in-event/constants';
import { ScannerDialog } from '../member-in-event/scanner-dialog';

import { useScanProfileCode } from './api';
import {
  SCAN_LABEL,
  SCAN_NEEDS_A_PHONE_BODY,
  SCAN_NEEDS_A_PHONE_HEADING,
  openConnectionLabel,
  profileScanConnectedMessage,
} from './constants';

/**
 * *Scan*, on Your account — `Web App.dc.html` §2.15's second header control.
 *
 * ## The button is drawn at every width; the scanner is not
 *
 * `MEM-057` AC-5, and `PLT-008` AC-4 behind it: **the code shows on desktop, the scanner stays
 * phone-only.** Those are two different rules and this is where they meet.
 *
 * The control is drawn at every width because §2.15 draws it there and because withholding it
 * would change the header's layout — `AGENTS.md`, 2026-08-29: what is withheld is the *figure*,
 * never the component. What is gated is the **viewfinder**, and the gate is
 * {@link useScannerAvailability}, which reads a camera, a secure context and `(pointer: coarse)`
 * and never a width. A narrow browser window on a laptop is not a phone, and an iPad in landscape
 * is wider than `md` and is exactly the device this is for.
 *
 * So on a device that cannot scan the button opens a sentence instead of a camera. That differs
 * from the Connect page, which draws a panel on the page rather than behind its control — the
 * difference is that Connect has a column to put a panel in and this is a header row with two
 * buttons on it, where a permanent explanatory panel would be chrome about a thing the member has
 * not asked for yet.
 *
 * ## What a successful scan does, and does not do
 *
 * It connects, and then it stops. There is no capture sheet: a note and a follow-up intent are
 * `MEM-029`'s, and they belong to a connection made *at an event* — this one has none, so there
 * is nothing to attach them to and no follow-up that would read them. The member gets the
 * sentence and a way to the connection, which is the only thing left to do.
 */
export function ProfileScanPanel(): React.ReactElement {
  const scannerIsUsable = useScannerAvailability();
  const scan = useScanProfileCode();
  const showError = useErrorToast();

  const [stage, setStage] = React.useState<ScanStage>(INITIAL_SCAN_STAGE);
  const [connected, setConnected] = React.useState<
    { readonly connectionRef: string; readonly firstName: string } | null
  >(null);
  const [explaining, setExplaining] = React.useState(false);

  React.useEffect(() => {
    if (scan.error !== null) showError(scan.error, 'That scan did not go through. Try again.');
  }, [scan.error, showError]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setConnected(null);
          if (scannerIsUsable) {
            setStage({ kind: 'scanning' });
            return;
          }
          setExplaining(true);
        }}
        className="inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-pill border-[1.5px] border-primary bg-card px-[18px] py-3 font-brand text-sm font-bold text-primary"
      >
        <ScanLine className="h-[17px] w-[17px]" aria-hidden />
        {SCAN_LABEL}
      </button>

      {scannerIsUsable ? (
        <ScannerDialog
          stage={stage}
          busy={scan.isPending}
          onClose={() => {
            setStage({ kind: 'idle' });
          }}
          onSubmitCode={(code) => {
            scan.mutate(code, {
              onSuccess: (outcome) => {
                if (outcome.result === 'refused') {
                  setStage({ kind: 'refused', message: outcome.message });
                  return;
                }
                // Connected, or already connected — both are the state the member asked for, and
                // `MEM-057` AC-6 makes the second one a report rather than a failure. The scanner
                // closes either way, because there is nothing more to do inside it.
                setConnected({
                  connectionRef: outcome.connectionRef,
                  firstName: outcome.firstName,
                });
                setStage({ kind: 'idle' });
              },
              onError: () => {
                setStage({ kind: 'unreachable' });
              },
            });
          }}
        />
      ) : null}

      {connected === null ? null : (
        <p
          role="status"
          className="w-full basis-full text-right text-sm text-muted-foreground md:text-right"
        >
          {profileScanConnectedMessage(connected.firstName)}{' '}
          <Link className="auth-ui-link" to={`/app/connections/${connected.connectionRef}`}>
            {openConnectionLabel(connected.firstName)}
          </Link>
        </p>
      )}

      <ScannerNeedsAPhoneDialog
        open={explaining}
        onOpenChange={(next) => {
          setExplaining(next);
        }}
      />
    </>
  );
}

/**
 * What a device with no camera is told (`MEM-057` AC-5, `MEM-056` AC-7).
 *
 * It names the way forward rather than the limitation: a member on a laptop can still *show* their
 * code from this same page, so the sentence points at the phone rather than apologising for the
 * desktop.
 */
function ScannerNeedsAPhoneDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
}): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[60] bg-[rgba(28,14,48,.55)] backdrop-blur-none" />
        <DialogBareContent
          data-testid="scanner-needs-a-phone"
          className="fixed inset-x-0 top-24 z-[61] mx-auto w-[calc(100%-2rem)] max-w-[420px] rounded-xl bg-card p-6 shadow-xl"
        >
          <DialogTitle className="font-brand text-lg font-bold text-foreground">
            {SCAN_NEEDS_A_PHONE_HEADING}
          </DialogTitle>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {SCAN_NEEDS_A_PHONE_BODY}
          </p>
          <DialogClose
            aria-label="Close"
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-pill text-text-subtle"
          >
            <X className="h-4 w-4" aria-hidden />
          </DialogClose>
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}
